import { useCallback, useMemo } from 'react';
import useComposerHotkeys from './useComposerHotkeys.js';
import { createChatComposerNumberHotkey, getChatComposerHotkey } from './chatComposerHotkeys.js';
import { MODE_KEYS, isSendTriggerEnabled } from './composerShared.jsx';

function resolveArray(value) {
  return Array.isArray(value) ? value : [];
}

function resolveObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function modeKeyFromEntry(entry = {}) {
  return String([
    entry?.key,
    entry?.value,
    entry?.modeKey,
    entry?.action?.target_mode,
  ].find(Boolean) ?? '').trim();
}

const MODE_HOTKEY_DEFAULTS = Object.freeze({
  composerActions: [],
  shortcutAction: null,
  shortcutActions: [],
  quickModeEntries: [],
  hasComposerChooser: false,
  disabled: false,
  loading: false,
  sendDisabled: false,
});

export default function useComposerModeHotkeys(props) {
  const {
  composerActions,
  shortcutAction,
  shortcutActions,
  quickModeEntries,
  hasComposerChooser,
  modeValue,
  onModeChange,
  onComposerActionSelect,
  disabled,
  loading,
  sendDisabled,
  onSend,
  } = { ...MODE_HOTKEY_DEFAULTS, ...props };
  const modeSuggestionActions = useMemo(() => {
    const orderedShortcutActions = resolveArray(shortcutActions).filter(resolveObject);
    if (orderedShortcutActions.length > 0) {
      return orderedShortcutActions;
    }
    if (shortcutAction && typeof shortcutAction === 'object') {
      return [shortcutAction];
    }
    if (!Array.isArray(composerActions)) {
      return [];
    }
    return composerActions
      .filter((entry) => String(entry?.source || '') === 'mode_suggestion')
      .filter(resolveObject);
  }, [composerActions, shortcutAction, shortcutActions]);

  const activateRequirementModeHotkey = getChatComposerHotkey('activate_requirement_mode');
  const dismissActiveModeHotkey = getChatComposerHotkey('dismiss_active_mode');
  const sendMessageHotkey = getChatComposerHotkey('send_message');

  const handleActivateModeByHotkey = useCallback((nextModeKey) => {
    if (!nextModeKey) {
      return;
    }
    if (modeValue === nextModeKey) {
      onModeChange?.(MODE_KEYS.auto);
      return;
    }
    onModeChange?.(nextModeKey);
  }, [modeValue, onModeChange]);

  const handleActivateRequirementMode = useCallback(() => {
    const firstSuggestionAction = modeSuggestionActions[0];
    if (firstSuggestionAction) {
      onComposerActionSelect?.(firstSuggestionAction);
      return;
    }
    const intakeEntry = resolveArray(quickModeEntries)
      .find((entry) => modeKeyFromEntry(entry) === MODE_KEYS.intake);
    handleActivateModeByHotkey(modeKeyFromEntry(intakeEntry) || MODE_KEYS.intake);
  }, [
    handleActivateModeByHotkey,
    modeSuggestionActions,
    onComposerActionSelect,
    quickModeEntries,
  ]);

  const orderedActionHotkeyBindings = useMemo(() => modeSuggestionActions
    .map((actionItem, index) => ({ actionItem, hotkey: createChatComposerNumberHotkey(index + 1, 'mode_suggestion') }))
    .filter(({ hotkey }) => Boolean(hotkey))
    .map(({ actionItem, hotkey }) => ({
      ...hotkey,
      when: () => !hasComposerChooser,
      onTrigger: () => onComposerActionSelect?.(actionItem),
    })), [
    hasComposerChooser,
    modeSuggestionActions,
    onComposerActionSelect,
  ]);

  const orderedModeHotkeyBindings = useMemo(() => resolveArray(quickModeEntries)
    .map((entry, index) => ({ modeKey: modeKeyFromEntry(entry), hotkey: createChatComposerNumberHotkey(index + 1, 'quick_mode') }))
    .filter(({ hotkey, modeKey }) => Boolean(hotkey && modeKey))
    .map(({ hotkey, modeKey }) => ({
      ...hotkey,
      when: () => !hasComposerChooser && modeSuggestionActions.length === 0,
      onTrigger: () => handleActivateModeByHotkey(modeKey),
    })), [
    handleActivateModeByHotkey,
    hasComposerChooser,
    modeSuggestionActions.length,
    quickModeEntries,
  ]);

  const modeHotkeyBindings = useMemo(() => ([
    ...orderedActionHotkeyBindings,
    ...orderedModeHotkeyBindings,
    {
      ...activateRequirementModeHotkey,
      when: () => !hasComposerChooser,
      onTrigger: () => handleActivateRequirementMode(),
    },
    {
      ...dismissActiveModeHotkey,
      when: () => {
        const normalizedModeValue = String(modeValue || '').trim();
        return Boolean(normalizedModeValue && normalizedModeValue !== MODE_KEYS.auto);
      },
      onTrigger: () => onModeChange?.(MODE_KEYS.auto),
    },
  ]), [
    activateRequirementModeHotkey,
    dismissActiveModeHotkey,
    handleActivateRequirementMode,
    hasComposerChooser,
    modeValue,
    onModeChange,
    orderedActionHotkeyBindings,
    orderedModeHotkeyBindings,
  ]);

  useComposerHotkeys({ enabled: true, bindGlobal: true, bindings: modeHotkeyBindings });

  const { handleKeyDown: handleInputKeyDown } = useComposerHotkeys({
    enabled: true,
    bindings: [
      ...modeHotkeyBindings,
      {
        ...sendMessageHotkey,
        when: (event) => isSendTriggerEnabled({
          hasComposerChooser,
          disabled,
          loading,
          sendDisabled,
          event,
        }),
        onTrigger: () => onSend?.(),
      },
    ],
  });

  return { handleInputKeyDown };
}
